import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  debounce,
  Tooltip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import SearchOutlined from "@ant-design/icons/SearchOutlined";
import ReloadOutlined from "@ant-design/icons/ReloadOutlined";
import { AiFillFileExcel } from "react-icons/ai";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import EmptyTable from "components/third-party/EmptyTable";
import { formatDateTimeIST } from "utils/date";
import api from "utils/api";
import {
  IEntryExitDashboardFilter,
  IEntryExitDashboardLogGridFilter,
  IEntryExitDashboardLogItem,
} from "../api/types";
import { useGetEntryExitLogs } from "../api/hooks";
import { PostModel } from "common/models/BaseModel";
import { ExportButton } from "components/ExportButton";

interface VisitorLogsTableProps {
  filter: IEntryExitDashboardFilter;
}

export default function VisitorLogsTable({
  filter,
}: VisitorLogsTableProps) {
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<number>(0);
  const [isExporting, setIsExporting] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "entryTime", sort: "desc" },
  ]);

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((val: string) => {
        setSearchText(val);
      }, 400),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSetSearchText(val);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    filter.fromDate,
    filter.toDate,
    filter.memberTypeID,
    filter.instituteID,
    filter.departmentID,
    filter.programID,
    statusFilter,
    searchText,
  ]);

  const postModel = useMemo<PostModel<IEntryExitDashboardLogGridFilter>>(() => {
    return {
      filterModel: {
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        memberTypeID: filter.memberTypeID,
        instituteID: filter.instituteID,
        departmentID: filter.departmentID,
        programID: filter.programID,
        statusFilter: statusFilter,
        searchText: searchText || undefined,
      },
      pagingModel: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      },
      sortModel: sortModel,
    };
  }, [filter, searchText, statusFilter, paginationModel, sortModel]);

  const { data, isLoading, isFetching, refetch } = useGetEntryExitLogs(postModel);

  const rowCountRef = useRef(data?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (data?.apiData?.count !== undefined) {
      rowCountRef.current = data?.apiData?.count;
    }
    return rowCountRef.current;
  }, [data?.apiData?.count]);

  const handlePagination = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const handleSorting = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  const formatDuration = (mins: number) => {
    if (!mins || mins <= 0) return "< 1 min";
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    if (hours > 0) return `${hours}h ${rem}m`;
    return `${rem} min${rem > 1 ? "s" : ""}`;
  };

  const handleExport = () => {
    setIsExporting(true);
    const payload = {
      entryDate: filter.fromDate ? new Date(filter.fromDate) : null,
      exitDate: filter.toDate ? new Date(filter.toDate) : null,
      instituteID: filter.instituteID,
      departmentID: filter.departmentID,
    };
    api.downloadExcel(
      "api/Reports/entryExitExports",
      "EntryExit_Attendance_Report",
      payload,
      setIsExporting
    );
  };

  const columns: GridColDef<IEntryExitDashboardLogItem>[] = useMemo(
    () => [
      {
        field: "memberName",
        headerName: "Member Name",
        flex: 1.4,
        minWidth: 160,
        renderCell: (params) => (
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {params.value || "-"}
            </Typography>
            {/* <Typography variant="caption" color="text.secondary">
              {params.row.memberTypeName || "Patron"}
            </Typography> */}
          </Box>
        ),
      },
      {
        field: "memberTypeName",
        headerName: "Member Type",
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => <>{params.value || "-"}</>,
      },
      {
        field: "memberCode",
        headerName: "Member Code",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.main" }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "departmentName",
        headerName: "Department",
        flex: 1.3,
        minWidth: 150,
        renderCell: (params) => <>{params.value || "-"}</>
      },
      {
        field: "instituteName",
        headerName: "Institute",
        flex: 1.4,
        minWidth: 150,
        renderCell: (params) => <>{params.value || "-"}</>,
      },
      {
        field: "entryTime",
        headerName: "Entry Time",
        headerAlign: "center",
        align: "center",
        flex: 0.8,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body1">
            {params.value ? formatDateTimeIST(params.value) : "-"}
          </Typography>
        ),
      },
      {
        field: "exitTime",
        headerName: "Exit Time",
        flex: 0.8,
        minWidth: 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          params.value ? (
            <Typography variant="body1">{formatDateTimeIST(params.value)}</Typography>
          ) : (
            <Chip label="Pending Exit" size="small" color="warning" variant="filled" sx={{ borderRadius: 10 }} />
          )
        ),
      },
      {
        field: "durationMinutes",
        headerName: "Duration",
        flex: 0.9,
        minWidth: 110,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {formatDuration(params.value)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.9,
        minWidth: 110,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Chip
            label={params.value === "Inside" ? "Inside" : "Exited"}
            size="small"
            color={params.value === "Inside" ? "success" : "default"}
            variant="filled"
            sx={{ fontWeight: 600, borderRadius: 10 }}
          />
        ),
      },
      {
        field: "remarks",
        headerName: "Source / Mode",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => {
          const isBiometric = params.value?.toLowerCase().includes("punching machine");
          return (
            <Tooltip title={params.value || "Manual Entry"}>
            <Chip
              label={isBiometric ? "Biometric Punch" : (params.value || "Manual Entry")}
              size="small"
              color={isBiometric ? "info" : "default"}
              variant={isBiometric ? "filled" : "outlined"}
              sx={{ borderRadius: 10, fontSize: "0.75rem" }}
            />
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  return (
    <Card elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h4" component={"h3"}>
            Visitor Activity Logs
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <FormControl
            size="medium"
            sx={{
              minWidth: { xs: "100%", sm: 140 },
              flex: { xs: "1 1 100%", sm: "none" },
            }}
          >
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(Number(e.target.value))}
            >
              <MenuItem value={0}>All Records</MenuItem>
              <MenuItem value={1}>Currently Inside</MenuItem>
              <MenuItem value={2}>Completed Exits</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="medium"
            placeholder="Search member, code..."
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 220 },
              flex: { xs: "1 1 100%", sm: "none" },
            }}
          />

          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<ReloadOutlined />}
            onClick={() => refetch()}
            disabled={isFetching}
            sx={{ flex: { xs: 1, sm: "none" } }}
          >
            Refresh
          </Button>
          <Tooltip title="Export to Excel of Entry/Exit Logs">
            <ExportButton
              groupName="EntryExitExport"
              fileName="EntryExit_Attendance_Report"
              reportTitle="Visitor Activity / Attendance Report"
              params={() => ({
                entryDate: filter.fromDate ?? null,
                exitDate: filter.toDate ?? null,
                instituteID: filter.instituteID ?? null,
                departmentID: filter.departmentID ?? null,
                memberID: null,
              })}
              disabled={rowCount === 0}
              buttonText="Excel"
              sx={{ flex: { xs: 1, sm: "none" } }}
            />
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container sx={{ height: { xs: 380, sm: 440, md: 480 } }}>
        <StyledDataGrid
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "wrap",
              fontWeight: 600,
              lineHeight: "normal",
              fontSize: "13px",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "wrap",
              alignContent: "center",
              fontSize: 14,
            },
          }}
          density="compact"
          disableRowSelectionOnClick
          getRowId={(row) => row.entryExitID}
          columns={columns}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePagination}
          sortModel={sortModel}
          onSortModelChange={handleSorting}
          getRowHeight={() => "auto"}
          paginationMode="server"
          sortingMode="server"
          rows={data?.apiData?.data || []}
          rowCount={rowCount}
          disableColumnFilter
          loading={isLoading || isFetching}
          slots={{
            noRowsOverlay: () => (
              <EmptyTable
                title="No Entry/Exit Logs Found"
                description="No attendance records match the selected date range and filter criteria."
              />
            ),
          }}
        />
      </Grid>
    </Card>
  );
}

